import type { Category } from '@scayle/storefront-nuxt'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import { isHiddenForMainNavigation } from './category'

/**
 * Checks if a navigation item should be displayed based on its active state and date range
 */
export function isNavigationItemActive(item: SbMainNavigation): boolean {
  if (!item || !item.active) return false

  const now = new Date()

  if (item.from) {
    // Storyblok stores UTC time, so we parse with 'Z'
    const fromDate = new Date(item.from + 'Z')
    if (now < fromDate) return false
  }

  if (item.till) {
    const tillDate = new Date(item.till + 'Z')
    if (now >= tillDate) return false
  }

  return true
}

/**
 * The main navigation flyout and the mask that dims the page behind it are
 * rendered by two different components, so both have to reach the same verdict
 * on whether a category has a flyout at all. While each carried its own copy of
 * the rule the header's copy ignored the scheduling window that the flyout's
 * copy honoured, so a campaign entry whose `till` had passed still counted as
 * content for the mask but not for the flyout: the page went behind a
 * full-viewport, click-swallowing overlay with nothing on top of it (ECOM-808).
 */
export function findFlyoutNavigationItem(
  mainNavigation: SbMainNavigation[] | undefined,
  category: FlyoutCategory | undefined,
): SbMainNavigation | undefined {
  if (!mainNavigation || !category) return undefined

  const categoryId = category.id?.toString()
  const categoryName = category.name?.toLowerCase()

  return mainNavigation.find(
    (item) =>
      isNavigationItemActive(item) &&
      ((!!item.RootCategoryID && item.RootCategoryID === categoryId) ||
        (!!item.label && item.label.toLowerCase() === categoryName)),
  )
}

/**
 * Whether the resolved entry has anything worth opening a flyout for. Scayle
 * children only count alongside a `RootCategoryID`, since that is what ties the
 * Storyblok entry to the category tree they come from.
 */
export function hasFlyoutContent(
  item: SbMainNavigation | undefined,
  category?: FlyoutCategory,
): boolean {
  if (!item) return false

  return Boolean(
    item.NavigationItem?.length ||
    item.NavigationFlyoutContent?.length ||
    item.ClickableImage?.length ||
    (item.RootCategoryID &&
      category?.children?.some((child) => !isHiddenForMainNavigation(child))),
  )
}

/** Shared shape of a Scayle `Category` and a mapped `NavigationItem`. */
interface FlyoutCategory {
  id?: number | string
  name?: string
  children?: Category[]
}

/**
 * Validates that navigation data has the required structure
 */
export function validateNavigationData(data: any): boolean {
  return !!(
    data?.content?.MainNavigation && Array.isArray(data.content.MainNavigation)
  )
}

/**
 * Resolves a Storyblok link to a standardized format, handling:
 * - Empty links
 * - Internal links
 * - Localized paths
 * - Category fallbacks
 */
export function resolveStoryblokLink(args: {
  link?: { cached_url?: string; linktype?: string; url?: string }
  category?: Category
  defaultPath?: string
}): string {
  const { link, category, defaultPath } = args
  const { toLocalePath } = useRouteHelpers()

  // Case 1: No link provided or empty internal link - use category path
  if (!link || (link.linktype === 'story' && !link.cached_url?.trim())) {
    if (category) {
      return linkCategoryTo(category).toString()
    }
    return defaultPath ? toLocalePath(defaultPath).toString() : ''
  }

  // Case 2: External link
  if (
    link.linktype === 'url' &&
    (link.url?.startsWith('http') || link.url?.startsWith('https'))
  ) {
    return link.url
  }

  // Case 3: Internal link with path
  const path = link.cached_url || link.url || ''
  if (path) {
    return toLocalePath(path).toString()
  }

  // Case 4: Fallback to category path if available
  if (category) {
    return linkCategoryTo(category).toString()
  }

  return ''
}

export const resolveNavigationPath = (link: any) => {
  if (!link?.cached_url) return ''

  return link.cached_url.startsWith('/')
    ? link.cached_url
    : '/' + link.cached_url
}

/** A Storyblok multilink as carried by a page's `redirectUrl` settings field. */
export type RedirectLink = {
  cached_url?: string
  linktype?: string
  url?: string
}

/**
 * The decision produced by {@link resolveRedirectTarget}. `none` means the page
 * renders normally; `internal`/`external` carry the resolved redirect target.
 */
export type RedirectDecision =
  | { kind: 'none' }
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string }

/**
 * Normalizes a path for the self-loop comparison (R5): drop query/hash and a
 * trailing slash (except root), so `/de/x`, `/de/x/` and `/de/x?p=1` all compare
 * equal. Mirrors how `trailingSlashRedirect.global.ts` strips trailing slashes,
 * so the two guards never bounce a slash-only variant between each other.
 */
function normalizeForLoopCheck(path: string): string {
  const [withoutQueryOrHash = ''] = path.split(/[?#]/)
  if (withoutQueryOrHash.length > 1 && withoutQueryOrHash.endsWith('/')) {
    return withoutQueryOrHash.replace(/\/+$/, '') || '/'
  }
  return withoutQueryOrHash
}

/**
 * Pure decision for ECOM-2385 page redirects. Given a page's `redirectUrl`
 * multilink, the active locale code, and the current request path, returns
 * where (if anywhere) the visitor should be redirected.
 *
 * Pure on purpose — no Nuxt/i18n runtime context — so it is unit-testable from
 * primitives. The composable `useStoryRedirect` performs the actual navigation.
 *
 * - Internal (`story`) targets resolve to `/${localeCode}/${cached_url}`
 *   (leading slashes normalized), matching the existing `scheduleGuard` pattern.
 * - External (`url`) targets are scheme-validated (R8): only `http(s)` is
 *   followed; any other scheme (`javascript:`, `data:`, …) yields `none`.
 * - A target resolving to the current path yields `none` (R5, self-loop guard).
 * - An absent/empty field yields `none` (R4).
 */
export function resolveRedirectTarget(
  redirectUrl: RedirectLink | undefined,
  localeCode: string,
  currentPath: string,
): RedirectDecision {
  if (!redirectUrl) {
    return { kind: 'none' }
  }

  // External link: only follow http(s); reject every other scheme.
  if (redirectUrl.linktype === 'url') {
    const url = redirectUrl.url?.trim()
    if (!url || !/^https?:\/\//i.test(url)) {
      return { kind: 'none' }
    }
    return { kind: 'external', url }
  }

  // Internal (story) link, or a link with a cached_url but no explicit linktype.
  //
  // A non-global multilink in a non-default locale degrades to an empty target:
  // the object is non-empty, but `cached_url` comes through as "", "/", or the
  // bare locale segment (the "old known issue"). Blindly prefixing that produced
  // a redirect to "/" — e.g. in `/fr` a content page bounced to `/fr/fr` — so
  // strip surrounding slashes and treat an empty slug, or one equal to the
  // locale code, as no target (ECOM-2385).
  const slug = redirectUrl.cached_url?.trim().replace(/^\/+|\/+$/g, '')
  // Case-insensitive so this holds for every locale code (`fr`, `at`, `fr-be`,
  // …), not just the one the bug first surfaced on, and tolerates a
  // mixed-case dimension slug (`fr-BE`).
  if (!slug || slug.toLowerCase() === localeCode.toLowerCase()) {
    return { kind: 'none' }
  }

  const path = `/${localeCode}/${slug}`
  if (normalizeForLoopCheck(path) === normalizeForLoopCheck(currentPath)) {
    return { kind: 'none' }
  }

  return { kind: 'internal', path }
}

/**
 * Origin of the Storyblok Visual Editor, the only document we ever expect to
 * embed a preview page. Previously duplicated as a bare string literal in both
 * apps' `CMSLink.vue`.
 */
export const STORYBLOK_EDITOR_ORIGIN = 'https://app.storyblok.com'

/**
 * A `window`-shaped object, narrowed to just what frame detection reads. Taking
 * this instead of the global keeps {@link detectStoryblokEditorClient} pure and
 * testable without jsdom — every property below can legally throw when the
 * embedder is cross-origin, which is the case the detection turns on.
 */
export interface StoryblokFrameWindow {
  self?: unknown
  top?: unknown
  parent?: { location?: { origin?: string } }
  location?: { ancestorOrigins?: ArrayLike<string> }
}

/**
 * Server-side half of the ECOM-2791 frame detection.
 *
 * The server cannot inspect the DOM, so it reads the navigation's destination
 * from Fetch Metadata (`Sec-Fetch-Dest`) instead: `document` for a top-level
 * navigation, `iframe` for a framed one. Getting this right server-side means
 * first paint already carries the correct markup — no flash of inert links and
 * no hydration mismatch.
 *
 * Anything other than `document` (a missing header on older WebKit, `empty` on
 * a payload prefetch, an unexpected value) resolves to "framed", preserving the
 * pre-ECOM-2791 behaviour; {@link detectStoryblokEditorClient} corrects it on
 * mount.
 */
export function resolveStoryblokFrameState(args: {
  isPreviewRoute: boolean
  secFetchDest?: string | null
}): boolean {
  const { isPreviewRoute, secFetchDest } = args

  // No `_storyblok` param means an ordinary page: never cripple its links.
  if (!isPreviewRoute) {
    return false
  }

  return secFetchDest?.trim().toLowerCase() !== 'document'
}

/**
 * Client-side half of the ECOM-2791 frame detection: are we a preview page
 * actually embedded by the Storyblok editor?
 *
 * Deliberately does *not* consult `window.StoryblokBridge`. `@storyblok/vue`
 * injects the bridge whenever the URL carries `_storyblok_tk`, which "open in
 * new tab" preserves — so the bridge is present out-of-frame too, and the check
 * that used to lead here short-circuited the whole ECOM-1672 fix.
 *
 * Once we know we are framed, an unreadable embedder resolves to `true`: a
 * `SecurityError` while probing the parent is itself evidence of a cross-origin
 * embedder, and the editor is the only frame we ship previews into. Firefox
 * exposes no `ancestorOrigins`, so in-editor previews there reach exactly that
 * throw — treating it as "not the editor" would make links live inside the
 * editor and break click-to-edit.
 */
export function detectStoryblokEditorClient(args: {
  isPreviewRoute: boolean
  win: StoryblokFrameWindow
}): boolean {
  const { isPreviewRoute, win } = args

  if (!isPreviewRoute) {
    return false
  }

  try {
    if (win.self === win.top) {
      return false
    }
  } catch {
    // Reading `top` threw, so something cross-origin holds us: fail closed.
    return true
  }

  try {
    const ancestorOrigins = win.location?.ancestorOrigins
    if (ancestorOrigins && ancestorOrigins.length > 0) {
      return Array.from(ancestorOrigins).includes(STORYBLOK_EDITOR_ORIGIN)
    }

    // Firefox and legacy browsers expose no `ancestorOrigins`.
    const parentOrigin = win.parent?.location?.origin
    if (typeof parentOrigin !== 'string') {
      // Framed by an embedder we cannot name at all: fail closed.
      return true
    }

    return parentOrigin === STORYBLOK_EDITOR_ORIGIN
  } catch {
    return true
  }
}
